import { endpoint, env, getAuthToken, handleUnauthorized } from '@eruvaka/web-pondlogs-base';
const api = (path) => endpoint(env.apiBaseUrl, path);
const normalizeProfilePhotoUrl = (value) => {
    if (!value)
        return undefined;
    const normalized = value.trim();
    if (!normalized)
        return undefined;
    const lower = normalized.toLowerCase();
    if (lower === 'default.png' ||
        lower.endsWith('/default.png') ||
        lower.includes('/settings/default.png')) {
        return undefined;
    }
    if (lower.startsWith('http://') ||
        lower.startsWith('https://') ||
        lower.startsWith('data:') ||
        lower.startsWith('blob:')) {
        return normalized;
    }
    return endpoint(env.apiBaseUrl, normalized);
};
const withNormalizedProfilePhoto = (profile) => ({
    ...profile,
    profilePhotoUrl: normalizeProfilePhotoUrl(profile.profilePhotoUrl),
});
const handleError = async (res, fallback) => {
    if (res.status === 401) {
        handleUnauthorized();
        throw new Error('Unauthorized');
    }
    let message = fallback;
    try {
        const body = (await res.json());
        message = body.message ?? body.error ?? fallback;
    }
    catch {
        /* ignore parse errors */
    }
    throw new Error(message);
};
const authFetch = async (url, options = {}) => {
    const token = getAuthToken();
    return fetch(url, {
        ...options,
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
    });
};
export const fetchUserProfile = async () => {
    const res = await authFetch(api('/api/v3/profile'));
    if (!res.ok)
        return handleError(res, 'Failed to fetch profile');
    const data = (await res.json());
    return withNormalizedProfilePhoto(data.data);
};
export const updateUserProfile = async (payload) => {
    const res = await authFetch(api('/api/v3/profile'), {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
    if (!res.ok)
        return handleError(res, 'Failed to update profile');
    const data = (await res.json());
    return withNormalizedProfilePhoto(data.data);
};
export const updateUserProfileWithMessage = async (payload) => {
    const res = await authFetch(api('/api/v3/profile'), {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
    if (!res.ok)
        return handleError(res, 'Failed to update profile');
    const data = (await res.json());
    return {
        user: withNormalizedProfilePhoto(data.data),
        message: data.message,
    };
};
export const updatePassword = async (payload) => {
    const res = await authFetch(api('/api/v3/profile/password'), {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
    if (!res.ok)
        return handleError(res, 'Failed to update password');
};
export const initiateEmailChange = async (newEmail) => {
    const res = await authFetch(api('/api/v3/profile/email'), {
        method: 'POST',
        body: JSON.stringify({ newEmail }),
    });
    if (!res.ok)
        return handleError(res, 'Failed to initiate email change');
};
export const initiatePhoneChange = async (newPhone) => {
    const res = await authFetch(api('/api/v3/profile/phone'), {
        method: 'POST',
        body: JSON.stringify({ newPhone }),
    });
    if (!res.ok)
        return handleError(res, 'Failed to initiate phone change');
};
export const verifyPhoneChange = async (otp) => {
    const res = await authFetch(api('/api/v3/profile/phone/verify'), {
        method: 'POST',
        body: JSON.stringify({ otp }),
    });
    if (!res.ok)
        return handleError(res, 'Failed to verify phone change');
};
export const verifyEmailChange = async (otp) => {
    const res = await authFetch(api('/api/v3/profile/email/verify'), {
        method: 'POST',
        body: JSON.stringify({ otp }),
    });
    if (!res.ok)
        return handleError(res, 'Failed to verify email change');
};
export const uploadProfilePhoto = async (file) => {
    const token = getAuthToken();
    const formData = new FormData();
    formData.append('avatar', file);
    const res = await fetch(api('/api/v3/profile/avatar'), {
        method: 'POST',
        credentials: 'include',
        // Do NOT set Content-Type — browser sets it with the multipart boundary
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
    });
    if (!res.ok)
        return handleError(res, 'Failed to upload profile photo');
    const data = (await res.json());
    return withNormalizedProfilePhoto(data.data);
};
